/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table, Drawer, Dropdown, Checkbox, Popconfirm, notification, Select, Tag, Tooltip,
  Divider,
} from 'antd';
import {
  EditOutlined, ControlOutlined, DeleteOutlined, FilterOutlined, FolderOpenOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWrench, faIndustry, faBolt, faPowerOff, faPlugCircleXmark, faCircleExclamation, faClock,
} from '@fortawesome/free-solid-svg-icons';
import H3Text from '@Uilib/h3Text';
import ResourceActions from '@Actions/production/resourceActions';
import closeIcon from '@Images/icons/icon-close-blue.png';
import H3Image from '@Uilib/h3Image';
import Helpers from '@Apis/helpers';
import searchIcon from '@Images/icons/icon-search.png';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import ResourceForm from '../ResourceForm';
import ResourceGroups from './ResourceGroups';
import PRZText from '../../../../Common/UI/PRZText';
import PRZSelect from '../../../../Common/UI/PRZSelect';
import useCheckPortrait from '../../../../../hooks/useCheckPortrait';
import './style.scss';
import { entityNameEnum, filterTypeEnums } from '../../../../../apis/constants';
import UserSavedFilterViewSelector from '../../../../Common/UserSavedFilterViewSelector';

const fixedColumns = ['RESOURCE_ID', 'RESOURCE_NAME'];

/**
 *
 */
function ResourceList({
  resources, resourcesLoading, user, history, deleteResource, deleteResourceLoading, getResource,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isUserReady, setIsUserReady] = useState(false);
  const [showNewResourceModal, setShowNewResourceModal] = useState(false);
  const [showUpdateResourceModal, setShowUpdateResourceModal] = useState(false);
  const [showResourceGroups, setShowResourceGroups] = useState(false);
  const [selectedResource, setSelectedResource] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [resType, setResType] = useState('');
  const [resStatus, setResStatus] = useState('');
  const [call, setCall] = useState(false);
  const [currentDefaultView, setCurrentDefaultView] = useState({});
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState([]);
  const [selectedFilterView, setSelectedFilterView] = useState({});
  const [isUserReadyInitial, setIsUserReadyInitial] = useState(false);
  const [isUpdateFilterSate, setIsUpdateFilterSate] = useState(true);
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [savedFilterViewsLoading, setSavedFilterViewsLoading] = useState(false);

  const {
    isMobile, isPortrait, isMobileAndPortrait, isMobileOrPortrait,
  } = useCheckPortrait();

  const [visibleColumns, setVisibleColumns] = useState(localStorage.getItem('prcz-page-columns-resource') ? JSON.parse(localStorage.getItem('prcz-page-columns-resource')) : {
    RESOURCE_ID: {
      label: 'Resource #',
      visible: true,
    },
    LOCATION: {
      label: 'Location',
      visible: true,
    },
    RESOURCE_NAME: {
      label: 'Resource Name',
      visible: true,
    },
    CAPACITY_PER_HOUR: {
      label: 'Capacity Per Hour',
      visible: false,
    },
    USED_IN_ROUTES: {
      label: 'Used In Routes',
      visible: false,
    },
    TYPE: {
      label: 'Work Center',
      visible: true,
    },
    OPERATIONAL_HOUR: {
      label: 'Operational Hrs',
      visible: true,
    },
    EFFICIENCY: {
      label: 'Efficiency',
      visible: true,
    },
    CREATED_DATE: {
      label: 'Created Date',
      visible: true,
    },
    STATUS: {
      label: 'Status',
      visible: true,
    },
  });

  const machineStatus = (status) => {
    if (status === 'ONLINE') return { bgColor: 'rgba(47,188,110,0.1)', color: '#2fbc6e' };
    if (status === 'BREAKDOWN') return { bgColor: 'rgba(255,86,48,0.1)', color: '#FF5630FF' };
    if (status === 'OFFLINE') return { bgColor: 'rgba(171,171,171,0.1)', color: '#c9c9c9' };
    return { bgColor: '#335aa8' };
  };

  const resourceType = (status) => {
    if (status === 'Machine') return { bgColor: 'rgba(45,124,247,0.2)', color: '#256ede' };
    if (status === 'Equipment') return { bgColor: 'rgba(227,145,52,0.1)', color: '#e39134' };
    if (status === 'Others') return { bgColor: 'rgba(239,200,40,0.2)', color: '#efc828' };
    return { bgColor: 'rgb(108,162,245)' };
  };

  const getResourcesColumns = () => {
    const resourceColumns = [
      {
        title: '',
        render: (record) => (
          <div className="mobile-list__card">
            <div className="mobile-list__card_header">
              <div className="mobile-list__card_header-left">
                <div
                  className="resource-table__icon"
                  style={{
                    backgroundColor: machineStatus(record?.status)?.bgColor,
                    color: machineStatus(record?.status)?.color,
                  }}
                >
                  {['OFFLINE'].includes(record?.status)
                    && <FontAwesomeIcon icon={faPowerOff} />}
                  <Tooltip title="This resource is online and available for use">
                    {['ONLINE'].includes(record?.status) && record?.occupancy_status === 'FREE'
                      && <FontAwesomeIcon icon={faPowerOff} />}
                  </Tooltip>
                  <Tooltip title="This resource is currently in use">
                    {['ONLINE'].includes(record?.status) && record?.occupancy_status === 'IN_USE'
                      && <FontAwesomeIcon icon={faClock} />}
                  </Tooltip>
                  {['BREAKDOWN'].includes(record?.status) && <FontAwesomeIcon icon={faPlugCircleXmark} />}
                </div>
                <PRZText className="mobile-list__card_item-number" text={`#${record?.resource_number ? record.resource_number : 'N/A'}`} />
              </div>
              <div className="mobile-list__card_header-right">
                <PRZText
                  style={{ backgroundColor: machineStatus(record.status).color }}
                  text={record?.status?.toProperCase()}
                  className="mobile-list__card__status"
                />
              </div>
            </div>
            <div className="mobile-list__card_body">
              <div className="mobile-list__card_body-left">
                <PRZText
                  text={`Location - ${record?.tenant_name || ''}`}
                  className="mobile-list__card_item-details"
                />
                <PRZText
                  text={`Resource Name - ${record?.resource_name || ''}`}
                  className="mobile-list__card_item-details"
                />
                <PRZText
                  text={`Resource Group - ${record?.resource_group_name || ''}`}
                  className="mobile-list__card_item-details"
                />
                {record?.linked_template_production_routes_count && (
                  <PRZText
                    text={`Used In Routes - ${record?.linked_template_production_routes_count || ''}`}
                    className="mobile-list__card_item-details"
                  />
                )}
                {record?.created_at && (
                  <PRZText
                    text={`Created Date - ${dayjs(record?.created_at).format('DD/MM/YYYY')}`}
                    className="mobile-list__card_item-details"
                  />
                )}
              </div>
              <div className="mobile-list__card_body-right">
                <Tooltip
                  color="white"
                  placement="top"
                  title={(
                    <PRZText className="mobile-list__card_item__tooltip_text-color" text="Capacity Per Hour" />
                  )}
                >
                  <PRZText
                    text={record?.type === 'Machine' ? record?.capacity_per_hour : '-'}
                    style={{ fontSize: '13px' }}
                    className="mobile-list__card_item-details"
                  />
                </Tooltip>
                <Tooltip
                  color="white"
                  placement="top"
                  title={(
                    <PRZText className="mobile-list__card_item__tooltip_text-color" text="Operational Hrs" />
                  )}
                >
                  <PRZText
                    text={record?.type === 'Machine' ? record?.ideal_op_time_hrs_per_day : '-'}
                    style={{ fontSize: '13px' }}
                    className="mobile-list__card_item-details"
                  />
                </Tooltip>
                <Tooltip
                  color="white"
                  placement="top"
                  title={(
                    <PRZText className="mobile-list__card_item__tooltip_text-color" text="Efficiency" />
                  )}
                >
                  <PRZText
                    text={record?.type === 'Machine' ? `${record?.ideal_time_efficiency * 100}%` : '-'}
                    style={{ fontSize: '13px' }}
                    className="mobile-list__card_item-details"
                  />
                </Tooltip>
              </div>
            </div>
            <Divider className="mobile-list__card_divider" />
            <div className="mobile-list__card_footer">
              <div className="mobile-list__card_footer-left">
                <Tooltip
                  color="white"
                  placement="top"
                  title={(
                    <PRZText className="mobile-list__card_item__tooltip_text-color" text="Finished Good" />
                  )}
                >
                  {
                    record?.fg_info?.map((rec) => (
                      <div className="me-table__fg-names">
                        <div>
                          <a>
                            {`#${rec?.internal_sku_code} ${rec?.ref_product_code ? `${rec?.ref_product_code} - ` : ''}${rec?.product_sku_name}`}
                          </a>
                        </div>
                      </div>
                    ))
                  }
                </Tooltip>
              </div>
              <div className="mobile-list__card_footer-right" />
            </div>
          </div>
        ),
        // responsive: ['xs'],
        hidden: !isMobile,
      },
      {
        title: '',
        width: '40px',
        render: (text, record) => (
          <div
            className="resource-table__icon"
            style={{
              backgroundColor: machineStatus(record?.status)?.bgColor,
              color: machineStatus(record?.status)?.color,
            }}
          >
            {['OFFLINE'].includes(record?.status)
              && <FontAwesomeIcon icon={faPowerOff} />}
            <Tooltip title="This resource is online and available for use">
              {['ONLINE'].includes(record?.status) && record?.occupancy_status === 'FREE'
                && <FontAwesomeIcon icon={faPowerOff} />}
            </Tooltip>
            <Tooltip title="This resource is currently in use">
              {['ONLINE'].includes(record?.status) && record?.occupancy_status === 'IN_USE'
                && <FontAwesomeIcon icon={faClock} />}
            </Tooltip>
            {['BREAKDOWN'].includes(record?.status) && <FontAwesomeIcon icon={faPlugCircleXmark} />}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        fixed: 'left',
      },
      {
        title: 'RESOURCE #',
        render: (text, record) => (
          <div className="min-width-85">
            {record?.resource_number}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.RESOURCE_ID?.visible ? 'display-none' : '',
      },
      {
        title: 'LOCATION',
        render: (record) => (
          <div className="min-width-70">
            <H3Text text={record?.tenant_name} />
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.LOCATION?.visible ? 'display-none' : '',
      },
      {
        title: 'MACHINE NAME',
        render: (text, record) => (
          <div className="min-width-100">
            {record?.resource_name}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.RESOURCE_NAME?.visible ? 'display-none' : '',
      },
      // {
      //   title: 'TYPE',
      //   render: (text, record) => (
      //     <div className="" style={{ display: 'flex', alignItems: 'center' }}>
      //       {record?.type === 'Others' && <FontAwesomeIcon icon={faBolt} />}
      //       {record?.type === 'Machine' && <FontAwesomeIcon icon={faIndustry} />}
      //       {record?.type === 'Equipment' && <FontAwesomeIcon icon={faWrench} />}
      //       &nbsp;
      //       {record?.type}
      //     </div>
      //   ),
      //   responsive: ['sm', 'md', 'lg', 'xxl'],
      // hidden: isMobile,
      //   className: !visibleColumns?.TYPE?.visible ? 'display-none' : '',
      // },
      {
        title: 'WORK CENTER',
        render: (record) => (
          <div className="min-width-85">
            {record?.resource_group_name}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.TYPE?.visible ? 'display-none' : '',
      },
      {
        title: 'CAPACITY PER HOUR',
        render: (text, record) => (
          <div className="min-width-130">
            {record?.type === 'Machine' ? record?.capacity_per_hour : '-'}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.CAPACITY_PER_HOUR?.visible ? 'display-none' : '',
      },
      {
        title: 'OPERATIONAL HRS',
        render: (text, record) => (
          <div className="min-width-100">
            {record?.type === 'Machine' ? record?.ideal_op_time_hrs_per_day : '-'}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.OPERATIONAL_HOUR?.visible ? 'display-none' : '',
      },
      {
        title: 'EFFICIENCY',
        render: (text, record) => (
          <div className="min-width-85">
            {record?.type === 'Machine' ? `${record?.ideal_time_efficiency * 100}%` : '-'}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.EFFICIENCY?.visible ? 'display-none' : '',
      },
      {
        title: 'CREATED DATE',
        render: (record) => (
          <div className="min-width-85">
            {dayjs(record?.created_at).format('DD/MM/YYYY')}
            <H3Text text={(record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown')} className="table-subscript" />
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.CREATED_DATE?.visible ? 'display-none' : '',
      },
      {
        title: 'USED IN ROUTES',
        render: (record) => (
          <div className="min-width-100">
            {record?.linked_template_production_routes_count}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.USED_IN_ROUTES?.visible ? 'display-none' : '',
      },
      {
        title: 'STATUS',
        render: (record) => (
          <div className="min-width-70">
            <div className="resource-status__wrapper">
              <div
                className="qr__status"
                style={{ backgroundColor: machineStatus(record?.status)?.color }}
              >
                {record?.status?.toProperCase()}
              </div>
            </div>
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobile,
        className: !visibleColumns?.STATUS?.visible ? 'display-none' : '',
      },
      {
        title: '',
        key: 'action',
        className: 'list__actions-column',
        fixed: 'right',
        render: (record) => (
          <div className="list__actions">
            {Helpers.getPermission(Helpers.permissionEntities.FACTORY_SETUP,
              Helpers.permissionTypes.UPDATE, user) && (
              <div
                className="list__actions-button list__actions-button-dark"
                onClick={() => {
                  setShowUpdateResourceModal(true);
                  setSelectedResource(record);
                }}
              >
                <EditOutlined />
              </div>
            )}
            {Helpers.getPermission(Helpers.permissionEntities.FACTORY_SETUP,
              Helpers.permissionTypes.DELETE, user) && (
              <Popconfirm
                placement="topRight"
                title="Are you sure you want to delete this Resource?"
                onConfirm={() => {
                  if (Number(record?.linked_template_production_routes_count) > 0) {
                    notification.open({
                      type: 'warning',
                      message: `This resource is in use by ${record?.linked_template_production_routes_count} production routes. Please remove the resource from the linked routes.`,
                      placement: 'top',
                      duration: 4,
                    });
                  } else {
                    const payload = {
                      org_id: user?.tenant_info?.org_id,
                      resource_id: record?.resource_id,
                    };
                    deleteResource(payload, () => {
                      setCall(!call);
                    });
                  }
                }}
                okText="Yes"
                cancelText="No"
              >
                <div
                  className="list__actions-button-delete"
                >
                  <DeleteOutlined />
                </div>
              </Popconfirm>
            )}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
        hidden: isMobileOrPortrait,
      },
    ];
    return resourceColumns.filter((item) => !item.hidden);
  };

  const pushParamsToUrl = (page, limit, word, currentTenant, resourceType, resourceStatus, selectedFilterView) => {
    let url = '';
    if (page) url += `?page=${page}`;
    if (limit) url += `&limit=${limit}`;
    if (word) url += `&keyword=${word}`;
    if (currentTenant) url += `&tenant=${currentTenant}`;
    if (resourceType) url += `&resourceType=${resourceType}`;
    if (resourceStatus) url += `&resourceStatus=${resourceStatus}`;
    if (selectedFilterView) url += `&filter_id=${selectedFilterView?.filter_id}`;
    history.push(url);
  };

  function getAppliedFiltersFromURL() {
    const appliedFiltersFromURL = [
      {
        filter_key: 'limit',
        filter_value: Number(Helpers.getValueFromUrl('limit') || limit),
        filter_type: filterTypeEnums.NUMBER,
      },
      {
        filter_key: 'keyword',
        filter_value: Helpers.getValueFromUrl('keyword') || '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'tenant',
        filter_value: Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'resourceType',
        filter_value: Helpers.getValueFromUrl('resourceType') || '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'resourceStatus',
        filter_value: Helpers.getValueFromUrl('resourceStatus') || '',
        filter_type: filterTypeEnums.STRING,
      },
    ];
    return appliedFiltersFromURL;
  }
  
  const initialFilterValues = useMemo(() => {
    return [
      {
        filter_key: 'limit',
        filter_value: limit,
        filter_type: filterTypeEnums.NUMBER,
      },
      {
        filter_key: 'keyword',
        filter_value: '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'tenant',
        filter_value: [user?.tenant_info?.tenant_id],
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'resourceType',
        filter_value: '',
        filter_type: filterTypeEnums.STRING,
      },
      {
        filter_key: 'resourceStatus',
        filter_value: '',
        filter_type: filterTypeEnums.STRING,
      },
    ];
  }, []);

  const systemDefaultUserView = useMemo(() => ({
    filter_id: 'system_default',
    filter_name: 'System Default View',
    entity_name: entityNameEnum.MACHINES,
    filters: initialFilterValues,
    tenant_id: user?.tenant_info?.tenant_id,
    org_id: user?.user_org_id,
    user_id: user?.user_id,
    is_default: false,
    is_system_default: true,
  }), []);
  
  function getFilterValueByKey({ filtersArray, key, valueOnly = true }) {
    const selectedFilter = filtersArray.find((item) => item.filter_key === key) || null;
    return valueOnly ? selectedFilter?.filter_value : selectedFilter;
  }

  useEffect(() => {
    if (savedFilterViews && isUserReadyInitial) {
      const defaultUserView = savedFilterViews.find((item) => item.is_default);

      if (defaultUserView) {
        setCurrentDefaultView(defaultUserView);
      } else {
        setCurrentDefaultView(systemDefaultUserView);
      }

      const appliedFiltersFromURL = getAppliedFiltersFromURL();

      const selectedFilterViewFromURL = Helpers.getValueFromUrl('filter_id');

      let currentSelectedView;
      if (selectedFilterViewFromURL) {
        currentSelectedView = savedFilterViews.find((item) => String(item.filter_id) === String(selectedFilterViewFromURL));
        if (!currentSelectedView) {
          currentSelectedView = systemDefaultUserView;
        }
      } else {
        currentSelectedView = defaultUserView || systemDefaultUserView;
      }

      const filterValues = (isFromURL) => {
        const currentSelectedViewFilters = currentSelectedView.filters;

        const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: isFromURL ? appliedFiltersFromURL : currentSelectedViewFilters, key: filterKey }) }), {});

        return filterValue;
      };

      const tempFilterValues = filterValues(selectedFilterViewFromURL);

      setSelectedFilterView(currentSelectedView);
      if (isUpdateFilterSate) {
        setCurrentlyAppliedFilters(appliedFiltersFromURL);

        setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
        setLimit(tempFilterValues.limit || limit);
        setSearchKeyword(tempFilterValues.keyword);
        setKeyword(tempFilterValues.keyword);
        setSelectedTenant(tempFilterValues.tenant);
        setResType(tempFilterValues.resourceType);
        setResStatus(tempFilterValues.resourceStatus);
        setShowNewResourceModal(Helpers.getValueFromUrl('toCreate') && Boolean(Helpers.getValueFromUrl('toCreate')));
      }

      if (!isUserReady) setIsUserReady(true);
      if (!isUpdateFilterSate) setIsUpdateFilterSate(true);
    }
  }, [savedFilterViews, isUserReadyInitial]);

  // useEffect(() => {
  //   if (user?.tenant_info?.org_id) {
  //     setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
  //     setLimit(Helpers.getValueFromUrl('limit') || limit);
  //     setSearchKeyword(Helpers.getValueFromUrl('keyword'));
  //     setKeyword(Helpers.getValueFromUrl('keyword'));
  //     setSelectedTenant(Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (Number(item))) : [user?.tenant_info?.tenant_id]);
  //     setResType(Helpers.getValueFromUrl('resourceType'));
  //     setResStatus(Helpers.getValueFromUrl('resourceStatus'));
  //     setIsUserReady(true);
  //     setShowNewResourceModal(Helpers.getValueFromUrl('toCreate') && Boolean(Helpers.getValueFromUrl('toCreate')));
  //   }
  // }, []);

  /**
 *
 */
  useEffect(() => {
    if (isUserReady) {
      pushParamsToUrl(currentPage, limit, searchKeyword, selectedTenant, resType, resStatus, selectedFilterView);
      setCurrentlyAppliedFilters(getAppliedFiltersFromURL());
      getResource(user?.tenant_info?.org_id, Helpers.getValueFromUrl('page'), Helpers.getValueFromUrl('limit'), Helpers.getValueFromUrl('keyword'), selectedTenant?.filter(
        (item) => !isNaN(item),
      )?.length > 0 ? selectedTenant?.join(',')
        : Helpers.getTenantEntityPermission(
          user?.user_tenants, Helpers.permissionEntities.FACTORY_SETUP, Helpers.permissionTypes.READ,
        )?.join(','), Helpers.getValueFromUrl('resourceType'), Helpers.getValueFromUrl('resourceStatus'));
    }
  }, [searchKeyword, currentPage, limit, isReady, selectedTenant, resType, resStatus, call, 
    // selectedFilterView
  ]);

  const { Option } = Select;

  const resourceTypeOptions = [
    { value: "", label: "All Resource Types" },
    { value: "Machine", label: "Machine" },
    { value: "Equipment", label: "Equipment" }
  ];

  const statusOptions = [
    { value: "", label: "All Resources" },
    { value: "ONLINE_IN_USE", label: "Online In Use" },
    { value: "ONLINE_FREE", label: "Online Free" },
    { value: "OFFLINE", label: "Offline" },
    { value: "BREAKDOWN", label: "Breakdown" }
  ];

  return (
    <Fragment>
      <div className="doc-list__wrapper doc-list__tab-content">
        <div className="ant-row">
          <div className="ant-col-xs-24">
            <div className="doc-list__header-wrapper">
              <div className="doc-list__header-wrapper__left">
                <div className="section-search">
                  <div className="doc-list__search-bar__wrapper">
                    <div className="section-search-bar">
                      <input
                        value={keyword}
                        placeholder="Search for resource..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setSearchKeyword(e.target.value);
                            setCurrentPage(1);
                          }
                        }}
                        onChange={(event) => {
                          setKeyword(event.target.value);
                        }}
                      />
                      <H3Image
                        src={searchIcon}
                        className="section-search-bar__icon"
                        alt="search"
                        onClick={() => {
                          setSearchKeyword(keyword);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="doc-list__header-wrapper__right">
                <div className="doc-list__buttons-wrapper">
                  <div
                    onClick={() => {
                      setShowResourceGroups(true);
                    }}
                    className="view-groups"
                  >
                    <FolderOpenOutlined />
                    {' '}
                    Work Centers&nbsp;
                  </div>
                </div>

                {Helpers.getPermission(Helpers.permissionEntities.FACTORY_SETUP, Helpers.permissionTypes.CREATE, user) && (
                  <div className="doc-list__buttons-wrapper">
                    <H3Button
                      buttonType={defaultButtonTypes.BLUE_ROUNDED}
                      text="+ New Machine"
                      onClick={() => {
                        setShowNewResourceModal(true);
                      }}
                      customClass="add-button hide__in-mobile"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex-align-c" style={{ gap: '10px' }}>
              <UserSavedFilterViewSelector
                selectedFilterView={selectedFilterView}
                onChangeTab={(currentSelectedView) => {
                  setSelectedFilterView(() => {
                    const currentSelectedViewFilters = currentSelectedView.filters;

                    const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: currentSelectedViewFilters, key: filterKey }) }), {});

                    const tempFilterValues = filterValue;
                    setCurrentPage(1);
                    setLimit(tempFilterValues.limit || limit);
                    setSearchKeyword(tempFilterValues.keyword);
                    setKeyword(tempFilterValues.keyword);
                    setSelectedTenant(tempFilterValues.tenant);
                    setResType(tempFilterValues.resourceType);
                    setResStatus(tempFilterValues.resourceStatus);

                    return currentSelectedView;
                  });
                }}
                currentlyAppliedFilters={currentlyAppliedFilters}
                entityName={entityNameEnum.MACHINES}
                setSavedFilterViews={(value) => setSavedFilterViews(value)}
                setSavedFilterViewsLoading={(value) => setSavedFilterViewsLoading(value)}
                setIsUpdateFilterSate={(value) => setIsUpdateFilterSate(value)}
                callback={() => {
                  if (!isUserReadyInitial) setIsUserReadyInitial(true);
                }}
                systemDefaultUserView={systemDefaultUserView}
              />
              <div className="header__tags">
                {searchKeyword && (
                  <Tag closable onClose={() => { setSearchKeyword(''); setKeyword(''); setCurrentPage(1); }} color="#2d7df7">
                    Search results for -
                    &nbsp;
                    <span><b><i>{searchKeyword}</i></b></span>
                  </Tag>
                )}
                {selectedTenant && selectedTenant?.map((item) => (
                  <Tag
                    key={item}
                    closable
                    onClose={() => {
                      setCurrentPage(1);
                      setSelectedTenant(selectedTenant.filter((tId) => tId !== item));
                    }}
                    color="#2d7df7"
                  >
                    Business Unit -
                    &nbsp;
                    <span>
                      <b>{user?.user_tenants?.find((tenant) => tenant?.tenant_id == item)?.tenant_name?.toUpperCase()}</b>
                    </span>
                  </Tag>
                ))}
                {resType && (
                  <Tag closable onClose={() => { setResType(''); setCurrentPage(1); }} color="#2d7df7">
                    Resource Type -
                    &nbsp;
                    <span><b>{resType?.toUpperCase()}</b></span>
                  </Tag>
                )}
                {resStatus && (
                  <Tag closable onClose={() => { setResStatus(''); setCurrentPage(1); }} color="#2d7df7">
                    Status -
                    &nbsp;
                    <span><b>{resStatus?.replaceAll('_', ' ')?.toUpperCase()}</b></span>
                  </Tag>
                )}
              </div>
            </div>
            <div className="doc-list__table-wrapper">
              <Table
                title={() => (
                  <div className="doc-list__table-header">
                    <div className="doc-list__table-header__left">
                      <React.Fragment>
                        {`${(resources && resources?.count) ? resources?.count : '0'} Machine(s)`}
                      </React.Fragment>
                    </div>
                    <div className="doc-list__table-header__right">
                      <div className="filters__buttons">
                        <Dropdown
                          trigger={['click']}
                          overlay={(
                            <div className="header__filter__container">
                              <div className="header__filter__container__title">
                                Apply Filters
                              </div>
                              <div className="filter__container filter__container-section">
                                <div className="filter__title">Business Unit</div>
                                <TenantSelector
                                  selectedTenant={selectedTenant}
                                  showSearch
                                  onChange={(value) => {
                                    setSelectedTenant(value);
                                  }}
                                  placeholder="Select Business Unit"
                                  isMultiple
                                  hideTitle
                                  includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.FACTORY_SETUP, Helpers.permissionTypes.READ)}
                                />
                              </div>
                              <div className="filter__container filter__container-section">
                                <div className="filter__title">Resource Type</div>
                                <PRZSelect
                                  value={resType}
                                  onChange={(value) => {
                                    setResType(value);
                                  }}
                                  placeholder="Filter By Status"
                                  options={resourceTypeOptions}
                                />
                              </div>
                              <div className="filter__container filter__container-section">
                                <div className="filter__title">Status</div>
                                <PRZSelect
                                  value={resStatus}
                                  onChange={(value) => {
                                    setResStatus(value);
                                  }}
                                  placeholder="Filter By Type"
                                  options={statusOptions}
                                />
                              </div>
                            </div>
                          )}
                        >
                          <div className="doc-list__action-buttons">
                            <div className="action-button">
                              <H3Text text="Filters" className="action-button-text" />
                              <FilterOutlined />
                            </div>
                          </div>
                        </Dropdown>
                        <Dropdown
                          overlay={(
                            <div className="custom-columns__content">
                              <div className="custom-columns__content-section">
                                <H3Text text="Visible Columns" className="custom-columns__content-title" />
                                {
                                  Object.keys(visibleColumns).filter((item) => visibleColumns[item]?.visible)?.map((key) => (
                                    <div key={key}>
                                      <Checkbox
                                        disabled={fixedColumns.includes(key)}
                                        checked={visibleColumns[key]?.visible}
                                        onChange={(value) => {
                                          const visibleColumnsCopy = JSON.parse(JSON.stringify(visibleColumns));
                                          visibleColumnsCopy[key].visible = !visibleColumnsCopy[key].visible;
                                          localStorage.setItem('prcz-page-columns-resource', JSON.stringify(visibleColumnsCopy));
                                          setVisibleColumns(visibleColumnsCopy);
                                        }}
                                      >
                                        {visibleColumns[key]?.label}
                                      </Checkbox>
                                    </div>
                                  ))
                                }
                              </div>
                              {Object.keys(visibleColumns).filter((item) => !visibleColumns[item]?.visible)?.length ? (
                                <div className="custom-columns__content-section">
                                  <H3Text text="Hidden Columns" className="custom-columns__content-title" />
                                  {
                                    Object.keys(visibleColumns).filter((item) => !visibleColumns[item]?.visible)?.map((key) => (
                                      <div key={key}>
                                        <Checkbox
                                          disabled={fixedColumns.includes(key)}
                                          checked={visibleColumns[key]?.visible}
                                          onChange={(value) => {
                                            const visibleColumnsCopy = JSON.parse(JSON.stringify(visibleColumns));
                                            visibleColumnsCopy[key].visible = !visibleColumnsCopy[key].visible;
                                            localStorage.setItem('prcz-page-columns-resource', JSON.stringify(visibleColumnsCopy));
                                            setVisibleColumns(visibleColumnsCopy);
                                          }}
                                        >
                                          {visibleColumns[key]?.label}
                                        </Checkbox>
                                      </div>
                                    ))
                                  }
                                </div>
                              ) : ''}
                            </div>
                          )}
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <div className="custom-columns__wrapper">
                            Customize Columns
                            <ControlOutlined />
                          </div>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                )}
                loading={resourcesLoading || deleteResourceLoading || savedFilterViewsLoading}
                showHeader
                size="small"
                scroll={!isMobileOrPortrait && resources && !!resources?.data?.length ? { x: 'max-content', y: 'calc(100dvh - 160px)' } : { x: 'max-content' }}
                sticky={!isMobileOrPortrait && resources && !!resources?.data?.length}
                columns={getResourcesColumns()}
                dataSource={resources && resources?.data ? resources?.data : []}
                bordered
                pagination={
                  {
                    defaultCurrent: currentPage,
                    current: currentPage,
                    total: resources ? resources?.count : 0,
                    pageSize: limit,
                    showTotal: (tot, range) => `${range[0]}-${range[1]} of ${tot} items`,
                    onChange: (page, pageSize) => {
                      setCurrentPage(page);
                      setLimit(pageSize);
                    },
                    position: ['bottomLeft'],
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100],
                  }
                }
                rowClassName={(record) => (record?.status === 'BREAKDOWN' ? 'resource-table__breakdown-row' : (record?.status === 'OFFLINE' ? 'resource-table__offline-row' : ''))}
              />
            </div>
          </div>
        </div>
      </div>
      <Drawer
        visible={showNewResourceModal}
        onClose={() => setShowNewResourceModal(false)}
        width="400px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '355px' }}>
            <H3Text text="Create Machine" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowNewResourceModal(false)} />
          </div>
        </div>
        <ResourceForm
          callback={() => {
            setCall(!call);
            setShowNewResourceModal(false);
          }}
        />
      </Drawer>
      <Drawer
        visible={showUpdateResourceModal}
        onClose={() => setShowUpdateResourceModal(false)}
        width="400px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '350px' }}>
            <H3Text text="Update Resource" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowUpdateResourceModal(false)} />
          </div>
        </div>
        <ResourceForm
          selectedResource={selectedResource}
          callback={() => {
            setCall(!call);
            setShowUpdateResourceModal(false);
          }}
        />
      </Drawer>
      <Drawer
        visible={showResourceGroups}
        onClose={() => setShowResourceGroups(false)}
        width="370px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '320px' }}>
            <H3Text text="Work Centers" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowResourceGroups(false)} />
          </div>
        </div>
        <ResourceGroups
          onClose={() => {
            setShowResourceGroups(false);
          }}
        />
      </Drawer>
    </Fragment>
  );
}

const mapStateToProps = ({ UserReducers, ResourceReducers }) => ({
  user: UserReducers.user,
  resourcesLoading: ResourceReducers.getResourceLoading,
  deleteResourceLoading: ResourceReducers.deleteResourceLoading,
  resources: ResourceReducers.resources,
});

const mapDispatchToProps = (dispatch) => ({
  getResource: (orgId, page, limit, searchKeyword, tenantIds, resourceType, resourceStatus) => dispatch(ResourceActions.getResource(orgId, page, limit, searchKeyword, tenantIds, resourceType, resourceStatus)),
  deleteResource: (payload, callback) => dispatch(ResourceActions.deleteResource(payload, callback)),
});

ResourceList.propTypes = {
  getResource: PropTypes.func,
  resources: PropTypes.any,
  user: PropTypes.any,
  resourcesLoading: PropTypes.bool,
  history: PropTypes.func,
  deleteResource: PropTypes.func,
  deleteResourceLoading: PropTypes.bool,

};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ResourceList));
